// React context exposing the current auth state. Single source of truth for
// "is the admin signed in" across the app — both the layout shell (showing
// the sign-out button) and the data-fetch layer (deciding whether to even
// attempt a request).

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { load as loadTokens } from './auth-storage';
import { refreshIfNeeded, signIn, signOut } from './auth-service';

interface AuthState {
  /** Up-to-date id_token, or null if the user isn't signed in. */
  idToken: string | null;
  /** Marker for the very first bootstrap fetch — UIs use this to render
   *  a blank screen instead of "signed out" before the refresh-on-mount
   *  has had a chance to run. */
  loading: boolean;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount, attempt a refresh. This covers (a) fresh browser open with
  // stored tokens that are still valid → idToken populates instantly, and
  // (b) stored tokens but expired → refresh recovers without a full sign-in,
  // (c) no stored tokens → loading flips off, idToken stays null.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const fresh = await refreshIfNeeded();
      if (!cancelled) {
        setIdToken(fresh);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // When the storage layer changes (post-callback save, post-signout clear),
  // pick that up. AuthCallbackPage triggers this by reloading the page after
  // the token exchange, but a manual storage event listener is cheap insurance.
  useEffect(() => {
    function onStorage() {
      const stored = loadTokens();
      setIdToken(stored?.idToken ?? null);
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      idToken,
      loading,
      signIn,
      signOut,
    }),
    [idToken, loading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
